/**
 * Score bands and status vocabulary.
 *
 * ONE ladder per concept, for the whole application. Partnership Net 2.0
 * carried six competing data-quality ladders across its components; the
 * prototype collapsed them to the two below and this port keeps that.
 *
 *   DCS band        High >= 85, Medium >= 70, Low below
 *   Confidence tier Ready >= 75, Estimated >= 55, Needs data below
 *
 * Colour is never the only signal. Every band ships an icon and a label, which
 * is why `StatusKind` maps to both.
 */

export type StatusKind = 'good' | 'warn' | 'bad' | 'info' | 'neutral';

export interface Band {
  readonly kind: StatusKind;
  readonly label: string;
}

/** Data Confidence Score band. */
export function dqsBand(score: number): Band {
  if (score >= 85) return { kind: 'good', label: 'High' };
  if (score >= 70) return { kind: 'warn', label: 'Medium' };
  return { kind: 'bad', label: 'Low' };
}

export type ConfidenceTierId = 'ready' | 'estimated' | 'needs-data';

export interface ConfidenceTier extends Band {
  readonly id: ConfidenceTierId;
}

/** Readiness tier for a confidence percentage. */
export function confTier(confidence: number): ConfidenceTier {
  if (confidence >= 75) return { kind: 'good', id: 'ready', label: 'Ready' };
  if (confidence >= 55) return { kind: 'warn', id: 'estimated', label: 'Estimated' };
  return { kind: 'bad', id: 'needs-data', label: 'Needs data' };
}

/** Representative confidence per tier, used when only the tier is known. */
export const TIER_CONFIDENCE: Record<ConfidenceTierId, number> = {
  ready: 84,
  estimated: 62,
  'needs-data': 38,
};

/**
 * Spread implied by a confidence level. Lower confidence widens the interval.
 * The 0.15 floor keeps a P10/P90 band visible even at 100% confidence — an
 * estimate is never presented as a point value.
 */
export const spreadFor = (confidence: number): number => (1 - confidence / 100) * 0.9 + 0.15;

export interface Interval {
  readonly p10: number;
  readonly p50: number;
  readonly p90: number;
}

/** Asymmetric interval around a central estimate; upside runs wider. */
export function band(p50: number, confidence: number): Interval {
  const spread = spreadFor(confidence);
  return {
    p10: Math.round(p50 * (1 - spread * 0.6)),
    p50: Math.round(p50),
    p90: Math.round(p50 * (1 + spread * 0.8)),
  };
}

/** Severity vocabulary shared by the Data QA rule library and the reports. */
export type Severity = 'critical' | 'serious' | 'warning' | 'info';

export const SEVERITY_ORDER: Record<Severity, number> = {
  critical: 0,
  serious: 1,
  warning: 2,
  info: 3,
};

export const SEVERITY_META: Record<Severity, Band> = {
  critical: { kind: 'bad', label: 'Critical' },
  serious: { kind: 'bad', label: 'Serious' },
  warning: { kind: 'warn', label: 'Warning' },
  info: { kind: 'info', label: 'Info' },
};

/** What a failing check does to the handover. */
export type RuleAction = 'block' | 'quarantine' | 'flag' | 'none';

export const ACTION_META: Record<RuleAction, Band> = {
  block: { kind: 'bad', label: 'Block the handover' },
  quarantine: { kind: 'warn', label: 'Quarantine the rows' },
  flag: { kind: 'info', label: 'Flag and continue' },
  none: { kind: 'good', label: 'No action' },
};

/** Band for a meter fill: full is good, and below 40 is a problem. */
export function meterKind(value: number): StatusKind | undefined {
  if (value === 100) return 'good';
  if (value >= 70) return undefined;
  if (value >= 40) return 'warn';
  return 'bad';
}
